<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, useNavigate } from 'svelte-navigator';
  import { Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Spinner } from '@client/components/spinner';

  import { ActionsCell, StatusCell } from '@client/pages/marketing/campaigns/manage/cell-formatters';

  import { CampaignsService } from '@client/services/campaigns';

  import { consolidateMetrics, formatRate } from '@client/utils/banners';
  import { determineLookBackPeriod } from '@client/utils/campaigns';

  import { PLUGIN_BADGES } from '@client/enums';

  import { formatDate } from '@common/utils/dates';
  import { shortenNumber } from '@common/utils/numbers';

  export let campaign = {};
  export let cache = {};
  export let onActionUpdate = () => {};
  export let onMessageSelect = null;
  export let scopedMetrics = undefined;
  export let onRename = null;

  const navigate = useNavigate();
  const redirect = (path) => navigate(path);

  const { basepath } = getContext('config');

  const getBadgeConfig = (type) => {
    return PLUGIN_BADGES[type] || { label: type };
  };

  const getMetrics = async (campaign, scopedMetrics) => {
    if (scopedMetrics !== undefined) return scopedMetrics;
    const id = campaign._id;

    if (!cache[id]) {
      const days = determineLookBackPeriod(campaign);
      const data = await CampaignsService.getMetrics({ aid: campaign._id, days });

      cache[id] = {
        metrics: consolidateMetrics(data, campaign)
      };
    }

    return cache[id].metrics;
  };

  const clickAction = () => (onMessageSelect ? onMessageSelect(campaign) : redirect(campaign.rulename));
</script>

<div class="campaign-card">
  <div class="campaign-card-header">
    <div class="campaign-card-rule" role="button" tabindex="0" on:click={clickAction}>
      <span title={campaign.rulename}>{campaign.rulename}</span>
      <StatusCell bind:row={campaign} />
    </div>
    <div class="campaign-card-actions">
      <ActionsCell
        row={campaign}
        props={{ isPerformance: !!onMessageSelect, update: onActionUpdate, onRename }}
        icon="os-icon-more-horizontal"
      />
    </div>
  </div>
  <div class="campaign-card-preview" role="button" tabindex="0" on:click={clickAction}>
    {#if campaign.adtype === 'image'}
      <img
        src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        class="campaign-card-banner"
        style="background: url({campaign.previewURL}) no-repeat center center;"
        alt={campaign.rulename}
      />
    {:else}
      <div class="campaign-card-text">{campaign.textstring}</div>
    {/if}
  </div>
  <div class="campaign-card-body" on:click={clickAction} role="button" tabindex="0">
    <b>{$format('label.SCHEDULE')}</b>
    <div class="schedule">
      {#if campaign.schedule}
        {formatDate(campaign.schedule.startDate, 'EEE, MMM d, yy @ h a')} - {formatDate(
          campaign.schedule.endDate,
          'EEE, MMM d, yy @ h a'
        )}
      {:else}
        <div class="none">{$format('label.NOT_SCHEDULED_CAMPAIGN')}</div>
      {/if}
    </div>
    <div class="bricks-container">
      <b>{$format('label.TAGS')}</b>
      {#if campaign.tags.length}
        <div class="bricks">
          {#each campaign.tags.slice(0, 4) as tag}
            <div class="brick">{tag.text}</div>
          {/each}
          {#if campaign.tags.length > 4}
            <div class="brick" id="tooltip-{campaign.rulename}-tags">
              +{campaign.tags.length - 4}
              {$format('label.MORE').toLowerCase()}
            </div>

            <Tooltip target="tooltip-{campaign.rulename}-tags">
              {@html campaign.tags
                .slice(4)
                .map(({ text }) => text)
                .join('<br />')}
            </Tooltip>
          {/if}
        </div>
      {:else}
        <div class="none">{$format('label.NO_ASSIGNED_TAGS')}</div>
      {/if}
    </div>
    <div class="bricks-container">
      <b>{$format('label.CATEGORIES')}</b>
      {#if campaign.category}
        <div class="bricks">
          {#each Object.keys(campaign.category).slice(0, 4) as category}
            <div class="brick">{category}</div>
          {/each}
          {#if Object.keys(campaign.category).length > 4}
            <div class="brick" id="tooltip-{campaign.rulename}-categories">
              +{Object.keys(campaign.category).length - 4}
              {$format('label.MORE').toLowerCase()}
            </div>

            <Tooltip target="tooltip-{campaign.rulename}-categories">
              {@html Object.keys(campaign.category)
                .slice(4)
                .map((category) => category)
                .join('<br />')}
            </Tooltip>
          {/if}
        </div>
      {:else}
        <div class="none">{$format('label.NO_ASSIGNED_CATEGORIES')}</div>
      {/if}
    </div>
    <div class="bricks-container">
      <b>{$format('label.INTEGRATIONS')}</b>
      {#if campaign?.targetingSources?.integrations?.campaigns?.length}
        <div class="integrations">
          {#each campaign.targetingSources.integrations.campaigns as campaign}
            <a use:link href="{basepath}/manage/integrations/{campaign.rulename}" class="btn btn-secondary btn-sm">
              <Icon name="box" />
              {$format(getBadgeConfig(campaign.provider).label)}
            </a>
          {/each}
        </div>
      {:else}
        <div class="none">{$format('label.NO_ASSIGNED_INTEGRATIONS')}</div>
      {/if}
    </div>
  </div>
  <div>
    {#await getMetrics(campaign, scopedMetrics)}
      <div class="metrics">
        <div class="loading">
          <Spinner show inline />
        </div>
      </div>
    {:then metrics}
      {#if metrics?.totals}
        <div class="metrics">
          <div class="metric">
            <b id="tooltip-{campaign.rulename}-delivered">{shortenNumber(metrics.totals.ad, 1)}</b>
            <div>{$format('label.DELIVERED')}</div>

            {#if metrics.totals.ad > 999}
              <Tooltip target="tooltip-{campaign.rulename}-delivered">
                {metrics.totals.ad.toLocaleString()}
              </Tooltip>
            {/if}
          </div>
          <div class="metric">
            <b>
              <span id="tooltip-{campaign.rulename}-views">
                {#if campaign.disableviews}
                  0
                {:else}
                  {shortenNumber(metrics.totals.combinedViews, 1)}
                {/if}
              </span>
            </b>
            <div>
              {$format('label.VIEWS')}
              {#if campaign.disableviews}
                <Icon id="disabled-views-{campaign._id}" name="alert-triangle" />

                <Tooltip target="disabled-views-{campaign._id}" placement="right">
                  {$format('label.TOOLTIP.DISABLED_VIEWS')}
                </Tooltip>
              {/if}
            </div>

            {#if metrics.totals.combinedViews > 999}
              <Tooltip target="tooltip-{campaign.rulename}-views">
                {metrics.totals.combinedViews.toLocaleString()}
              </Tooltip>
            {/if}
          </div>
          <div class="metric">
            <b id="tooltip-{campaign.rulename}-clicks">{shortenNumber(metrics.totals.combinedClicks, 1)}</b>
            <div>
              {$format('label.CLICKS')}
              {#if campaign.disableviews}
                <Icon id="no-ctr-{campaign._id}" name="alert-triangle" />
                <Tooltip target="no-ctr-{campaign._id}" placement="right">
                  {$format('label.TOOLTIP.NO_CTR')}
                </Tooltip>

                <small>(CTRR {formatRate(metrics.totals.ctrr)})</small>
              {:else}
                <small>(CTR {formatRate(metrics.totals.combinedCtr)})</small>
              {/if}
            </div>

            {#if metrics.totals.combinedClicks > 999}
              <Tooltip target="tooltip-{campaign.rulename}-clicks">
                {metrics.totals.combinedClicks.toLocaleString()}
              </Tooltip>
            {/if}
          </div>
          <div class="metric">
            <b id="tooltip-{campaign.rulename}-reach">{shortenNumber(metrics.calculated.reach, 1)}</b>
            <div>{$format('label.REACH')}</div>

            {#if metrics.calculated.reach > 999}
              <Tooltip target="tooltip-{campaign.rulename}-reach">
                {metrics.calculated.reach.toLocaleString()}
              </Tooltip>
            {/if}
          </div>
        </div>
      {:else}
        <div class="metrics"><div class="loading"><Spinner show inline /></div></div>
      {/if}
    {/await}
  </div>
</div>

<style lang="scss" src="./campaign-card.scss"></style>
